import React, { useState, useRef } from 'react';
import axios from 'axios';
import { FiUploadCloud, FiX } from 'react-icons/fi';
import { showNotification } from './Notification';
import ProgressBar from './ProgressBar';
import { groupValidationErrors } from './Trainees';

// A second, independent entry point into the same /api/trainees/upload/*
// endpoints as the original Import button, built from scratch with its own
// state and handlers so it shares no wiring with the old flow - only the
// grouped-error display helper is reused since it's pure formatting logic,
// not part of the old button's plumbing. Every network failure here surfaces
// the raw error (message + code) to the admin instead of failing quietly,
// since a silent failure with nothing in the server logs was the original
// button's exact symptom.
const STEP_PICK = 'pick';
const STEP_MAPPING = 'mapping';
const STEP_RESULT = 'result';

const ImportTraineesNew = ({ batches, onImported }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(STEP_PICK);
  const [batchId, setBatchId] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ percent: 0, message: '' });

  const [fileToken, setFileToken] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [columnMapping, setColumnMapping] = useState([]);
  const [validation, setValidation] = useState(null);

  const fileInputRef = useRef(null);

  const resetAll = () => {
    setStep(STEP_PICK);
    setBatchId('');
    setBusy(false);
    setProgress({ percent: 0, message: '' });
    setFileToken(null);
    setHeaders([]);
    setFieldDefinitions([]);
    setColumnMapping([]);
    setValidation(null);
  };

  const close = () => {
    setIsOpen(false);
    resetAll();
  };

  const describeError = (error) => {
    if (error.response) {
      return error.response.data?.error || `Server returned ${error.response.status}`;
    }
    if (error.request) {
      return `No response from server (${error.code || 'network error'}). The request may have timed out or been dropped before reaching Railway.`;
    }
    return error.message || 'Unknown error';
  };

  const handlePickFile = () => {
    if (!batchId) {
      showNotification('Select a batch first', 'error');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    setBusy(true);
    setProgress({ percent: 10, message: `Sending ${file.name} for header detection...` });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('batch_id', batchId);

    try {
      const { data } = await axios.post('/api/trainees/upload/validate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000
      });

      if (!data.needsMapping) {
        throw new Error('Unexpected response from server while reading the file.');
      }

      setFileToken(data.fileToken);
      setHeaders(data.headers || []);
      setFieldDefinitions(data.fieldDefinitions || []);
      setColumnMapping(data.suggestedMapping || []);
      setStep(STEP_MAPPING);
    } catch (error) {
      console.error('[ImportTraineesNew] header detection failed:', error);
      showNotification(describeError(error), 'error', 8000);
    } finally {
      setBusy(false);
      setProgress({ percent: 0, message: '' });
    }
  };

  const handleMappingChange = (index, value) => {
    setColumnMapping(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleRunValidation = async () => {
    const requiredKeys = fieldDefinitions.filter(f => f.required).map(f => f.key);
    const mappedKeys = new Set(columnMapping.filter(Boolean));
    const missing = requiredKeys.filter(k => !mappedKeys.has(k));
    if (missing.length > 0) {
      const labels = fieldDefinitions.filter(f => missing.includes(f.key)).map(f => f.label).join(', ');
      showNotification(`Map these required fields before continuing: ${labels}`, 'error');
      return;
    }

    setBusy(true);
    setProgress({ percent: 30, message: 'Validating rows (this can take a few seconds for large files)...' });

    try {
      const formData = new FormData();
      formData.append('fileToken', fileToken);
      formData.append('batch_id', batchId);
      formData.append('columnMapping', JSON.stringify(columnMapping));

      const { data } = await axios.post('/api/trainees/upload/validate', formData, {
        timeout: 600000
      });

      setValidation({ isValid: data.isValid, total: data.total, errors: data.errors || [] });
      setStep(STEP_RESULT);
    } catch (error) {
      console.error('[ImportTraineesNew] validation failed:', error);
      showNotification(describeError(error), 'error', 8000);
    } finally {
      setBusy(false);
      setProgress({ percent: 0, message: '' });
    }
  };

  const handleConfirm = async (skipInvalidRows) => {
    setBusy(true);
    setProgress({ percent: 20, message: skipInvalidRows ? 'Skipping invalid rows and importing the rest...' : 'Importing...' });

    try {
      const { data } = await axios.post('/api/trainees/upload/confirm', {
        fileToken,
        batchId,
        columnMapping,
        skipInvalidRows
      }, { timeout: 600000 });

      showNotification(data.message, 'success', 6000);
      close();
      onImported?.();
    } catch (error) {
      console.error('[ImportTraineesNew] confirm failed:', error);
      showNotification(describeError(error), 'error', 8000);
    } finally {
      setBusy(false);
      setProgress({ percent: 0, message: '' });
    }
  };

  return (
    <>
      <button
        className="btn btn-secondary"
        style={{ marginRight: '10px' }}
        onClick={() => setIsOpen(true)}
        disabled={busy}
      >
        <FiUploadCloud /> Import Trainees (New)
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls,.ods"
        onChange={handleFileChosen}
        style={{ display: 'none' }}
      />

      {isOpen && (
        <div className="modal" onClick={() => !busy && close()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Import Trainees</h2>
              <button className="close-btn" onClick={close} disabled={busy}>
                <FiX />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {busy && (
                <div style={{ marginBottom: '20px' }}>
                  <ProgressBar progress={progress.percent} message={progress.message} />
                </div>
              )}

              {step === STEP_PICK && (
                <div>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Select Batch *</label>
                    <select
                      value={batchId}
                      onChange={(e) => setBatchId(e.target.value)}
                      style={{ width: '100%', padding: '10px', fontSize: '16px' }}
                    >
                      <option value="">-- Select a Batch --</option>
                      {Array.isArray(batches) && batches.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary, #6c757d)' }}>
                    Accepted file types: .csv, .xlsx, .xls, .ods. Column headers don't need to match exactly - the next step lets you map whatever headers your file has.
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button className="btn btn-primary" onClick={handlePickFile} disabled={busy || !batchId}>
                      Choose File
                    </button>
                  </div>
                </div>
              )}

              {step === STEP_MAPPING && (
                <div>
                  <p>Map each column in your file to a field. Required fields are marked with *.</p>
                  <div style={{ maxHeight: '350px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--hover-bg, #f8f9fa)' }}>
                          <th style={{ textAlign: 'left', padding: '8px 12px' }}>File Column</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px' }}>Maps To</th>
                        </tr>
                      </thead>
                      <tbody>
                        {headers.map((h, idx) => (
                          <tr key={idx} style={{ borderTop: '1px solid #eee' }}>
                            <td style={{ padding: '8px 12px' }}>{h}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <select
                                value={columnMapping[idx] || ''}
                                onChange={(e) => handleMappingChange(idx, e.target.value)}
                                style={{ width: '100%', padding: '6px' }}
                              >
                                <option value="">-- Ignore this column --</option>
                                {fieldDefinitions.map(f => (
                                  <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                    <button className="btn btn-secondary" onClick={close} disabled={busy}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleRunValidation} disabled={busy}>Validate</button>
                  </div>
                </div>
              )}

              {step === STEP_RESULT && validation && (
                validation.isValid ? (
                  <div>
                    <div className="alert alert-success" style={{ padding: '15px', marginBottom: '20px', borderRadius: '4px', backgroundColor: '#d4edda', color: '#155724' }}>
                      <h4>Validation Successful</h4>
                      <p>All <strong>{validation.total}</strong> records passed validation.</p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                      <button className="btn btn-secondary" onClick={close} disabled={busy}>Cancel</button>
                      <button className="btn btn-primary" onClick={() => handleConfirm(false)} disabled={busy}>Confirm & Import</button>
                    </div>
                  </div>
                ) : (() => {
                  const validCount = validation.total - validation.errors.length;
                  const groups = groupValidationErrors(validation.errors);
                  const skippableCount = validation.errors.filter(e => e.skippable).length;
                  return (
                    <div>
                      <div className="alert alert-danger" style={{ padding: '15px', marginBottom: '20px', borderRadius: '4px', backgroundColor: '#f8d7da', color: '#721c24' }}>
                        <h4>Validation Failed</h4>
                        <p>
                          <strong>{validCount}</strong> of <strong>{validation.total}</strong> rows are ready to import.{' '}
                          <strong>{validation.errors.length}</strong> rows have errors.
                        </p>
                      </div>
                      <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', marginBottom: '20px' }}>
                        {groups.map((g, idx) => (
                          <div key={idx} style={{ padding: '10px 14px', borderBottom: idx < groups.length - 1 ? '1px solid #dee2e6' : 'none' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                              <span style={{ fontWeight: 600, color: '#721c24' }}>{g.message}</span>
                              <span style={{ background: '#721c24', color: '#fff', padding: '2px 10px', borderRadius: '999px', fontSize: '0.85em' }}>
                                {g.count} {g.count === 1 ? 'row' : 'rows'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.85em', color: '#6c757d', marginTop: '4px' }}>{g.rangeLabel}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button className="btn btn-secondary" onClick={close} disabled={busy}>Cancel</button>
                        {skippableCount > 0 && (
                          <button
                            className="btn btn-primary"
                            title={`Skips the ${skippableCount} blank placeholder row${skippableCount === 1 ? '' : 's'} and imports the ${validCount} valid rows. Rows with other errors are left out and reported back.`}
                            onClick={() => handleConfirm(true)}
                            disabled={busy}
                          >
                            Skip {skippableCount} Blank Row{skippableCount === 1 ? '' : 's'} &amp; Import Rest
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImportTraineesNew;
